import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Users, ChevronLeft, ChevronRight, X, Clock, MapPin, Video, ExternalLink, Search, QrCode, Copy, Share2, Link2, Pencil, Lock, Loader2, Mail, AlertTriangle, Power, PowerOff, Zap, HelpCircle } from 'lucide-react';
import { formatDDMMYYYY } from '../../utils/date';
import { fmtCupo } from '../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const toTitleCase = (s) => {
  if (!s) return '';
  return s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
};

const formatRut = (rut) => {
  if (!rut) return '';
  const clean = rut.replace(/[.\-]/g, '');
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formatted}-${dv}`;
};

const DAYS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function CapacitadorCalendario() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [sesiones, setSesiones] = useState([]);
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [showDetalle, setShowDetalle] = useState(null);
  const [editModal, setEditModal] = useState(null);   // {sesion, form}
  const [scopeModal, setScopeModal] = useState(null); // {form, count_future}
  const [inscripciones, setInscripciones] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showInscribir, setShowInscribir] = useState(null);
  const [conflictoInscribir, setConflictoInscribir] = useState(null); // {message, conflictos:[{rut, nombre, sesion_id, fecha_sesion, sesion_estado, nomina_cerrada}]}
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [workers, setWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [workerSearch, setWorkerSearch] = useState('');
  const [form, setForm] = useState({ capacitacion_id: '', fecha: '', fechas_extra: [], hora_inicio: '08:00', hora_fin: '17:00', cupo_maximo: 50, lugar: 'E-Learning', url_sesion: '', notas: '' });
  const [moreDayModal, setMoreDayModal] = useState(null); // { dateStr, sesiones }
  const [cierrePruebaHora, setCierrePruebaHora] = useState('15:00');
  const [qrModal, setQrModal] = useState(null); // { sesion, url, qr_data_url, loading }
  const [closeModal, setCloseModal] = useState(null); // { sesion } - modal de confirmación cerrar nómina
  const [closingBusy, setClosingBusy] = useState(false);
  const [previewData, setPreviewData] = useState(null); // {subject, html, destinatarios, total, con_email, sin_email, sender}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pruebaEstado, setPruebaEstado] = useState(null); // {activa, modo, motivo, ventana} para showDetalle
  const [togglingPrueba, setTogglingPrueba] = useState(false);
  const [showCerradas, setShowCerradas] = useState(false);
  const [showAyuda, setShowAyuda] = useState(false);

  const openPreview = async (sesion) => {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/capacitador/sesiones/${sesion.id}/email-preview`, { headers });
      setPreviewData({ ...res.data, _sesion: sesion });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar la vista previa');
    }
    setPreviewLoading(false);
  };

  const handleCerrarNomina = async () => {
    if (!closeModal) return;
    setClosingBusy(true);
    try {
      const res = await axios.post(
        `${API}/capacitador/sesiones/${closeModal.sesion.id}/cerrar-nomina`,
        {},
        { headers }
      );
      const d = res.data || {};
      let msg = d.message || 'Nómina cerrada';
      if (d.enviados) msg += ` Notificaciones enviadas: ${d.enviados}/${d.con_email}.`;
      if (d.sin_email) msg += ` Sin email: ${d.sin_email}.`;
      if (d.fallidos) msg += ` Fallidas: ${d.fallidos}.`;
      toast.success(msg, { duration: 6000 });
      setCloseModal(null);
      setShowDetalle(null);
      fetchSesiones();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cerrar nómina');
    }
    setClosingBusy(false);
  };

  const openQrModal = async (sesion) => {
    setQrModal({ sesion, url: '', qr_data_url: '', loading: true });
    try {
      const res = await axios.get(`${API}/sesiones/${sesion.id}/prueba-publica`, { headers });
      setQrModal({ sesion, url: res.data.url, qr_data_url: res.data.qr_data_url, loading: false,
                   prueba_activa: res.data.prueba_activa, prueba_modo: res.data.prueba_modo,
                   prueba_motivo: res.data.prueba_motivo, prueba_ventana: res.data.prueba_ventana });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar QR');
      setQrModal(null);
    }
  };

  const fetchPruebaEstado = useCallback(async (sesionId) => {
    if (!sesionId) return;
    try {
      const res = await axios.get(`${API}/sesiones/${sesionId}/prueba-estado`, { headers });
      setPruebaEstado(res.data);
    } catch { setPruebaEstado(null); }
  }, [token]);

  const togglePrueba = async (sesionId, estado) => {
    setTogglingPrueba(true);
    try {
      const res = await axios.post(`${API}/sesiones/${sesionId}/prueba-toggle`, { estado }, { headers });
      setPruebaEstado(res.data);
      // Sync qr modal si está abierto
      if (qrModal && qrModal.sesion?.id === sesionId) {
        setQrModal(q => ({ ...q, prueba_activa: res.data.activa, prueba_modo: res.data.modo,
                            prueba_motivo: res.data.motivo, prueba_ventana: res.data.ventana }));
      }
      toast.success(res.data.activa ? 'Prueba ACTIVA' : (estado === 'auto' ? 'Modo automático (13:00–15:00)' : 'Prueba INACTIVA'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar estado de la prueba');
    }
    setTogglingPrueba(false);
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copiado al portapapeles'); }
    catch { toast.error('No se pudo copiar'); }
  };

  const downloadQr = (qrDataUrl, name) => {
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `prueba-qr-${name || 'sesion'}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const fetchReglas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/capacitador/reglas`, { headers });
      const r = (res.data || []).find(x => x.clave === 'cierre_prueba_hora');
      if (r) setCierrePruebaHora(r.valor || '15:00');
    } catch { }
  }, [token]);

  const fetchSesiones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sesiones`, { headers });
      setSesiones(res.data);
    } catch { }
  }, [token]);

  const fetchCapacitaciones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/capacitaciones`, { headers });
      setCapacitaciones(res.data.filter(c => c.calendarizable));
    } catch { }
  }, [token]);

  const fetchEmpresas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras`, { headers });
      setEmpresas(res.data || []);
    } catch { }
  }, [token]);

  useEffect(() => { fetchSesiones(); fetchCapacitaciones(); fetchEmpresas(); fetchReglas(); }, [fetchSesiones, fetchCapacitaciones, fetchEmpresas, fetchReglas]);

  useEffect(() => {
    if (showDetalle?.id) fetchPruebaEstado(showDetalle.id);
    else setPruebaEstado(null);
  }, [showDetalle?.id, fetchPruebaEstado]);

  const handleCreate = async (e) => {
    e.preventDefault();
    // Reunir fechas: base `form.fecha` + `form.fechas_extra`
    const fechas = Array.from(new Set([form.fecha, ...(form.fechas_extra || [])].filter(Boolean))).sort();
    if (fechas.length === 0) { toast.error('Selecciona al menos una fecha'); return; }
    // Validar rango máximo 30 días entre la primera y la última
    if (fechas.length > 1) {
      const d0 = new Date(fechas[0] + 'T00:00');
      const d1 = new Date(fechas[fechas.length - 1] + 'T00:00');
      const dias = Math.round((d1 - d0) / 86400000);
      if (dias > 30) { toast.error('El rango entre la primera y última fecha no puede superar 30 días'); return; }
    }
    const { fechas_extra, ...base } = form;   // eslint-disable-line no-unused-vars
    try {
      let ok = 0;
      for (const f of fechas) {
        try {
          await axios.post(`${API}/sesiones`, { ...base, fecha: f }, { headers });
          ok += 1;
        } catch (err) {
          toast.error(`${f}: ${err.response?.data?.detail || 'Error'}`);
        }
      }
      if (ok > 0) toast.success(`${ok} sesión${ok !== 1 ? 'es' : ''} creada${ok !== 1 ? 's' : ''}`);
      if (ok === fechas.length) {
        setShowModal(false);
        setForm({ capacitacion_id: '', fecha: '', fechas_extra: [], hora_inicio: '08:00', hora_fin: '17:00', cupo_maximo: 50, lugar: 'E-Learning', url_sesion: '', notas: '' });
      }
      fetchSesiones();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear sesion'); }
  };

  const openEdit = (s) => {
    setEditModal({
      sesion: s,
      form: {
        capacitacion_id: s.capacitacion_id,
        fecha: s.fecha,
        hora_inicio: s.hora_inicio,
        hora_fin: s.hora_fin,
        cupo_maximo: s.cupo_maximo,
        lugar: s.lugar || '',
        url_sesion: s.url_sesion || '',
        notas: s.notas || '',
      },
    });
  };

  const submitEdit = async (form, scope) => {
    try {
      await axios.put(`${API}/sesiones/${editModal.sesion.id}?scope=${scope}`, form, { headers });
      toast.success(scope === 'future' ? 'Sesión y futuras actualizadas' : 'Sesión actualizada');
      setEditModal(null);
      setScopeModal(null);
      setShowDetalle(null);
      fetchSesiones();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDelete = (id) => {
    setConfirmAction({
      message: '¿Eliminar esta sesion? Esta accion no se puede deshacer.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/sesiones/${id}`, { headers });
          toast.success('Sesion eliminada');
          fetchSesiones();
          setShowDetalle(null);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmAction(null);
      }
    });
  };

  const handleCancelInscripcion = (inscId) => {
    setConfirmAction({
      message: '¿Cancelar esta inscripcion? El cupo sera liberado.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/sesiones/inscripcion/${inscId}`, { headers });
          toast.success('Inscripcion cancelada');
          fetchSesiones();
          if (showDetalle) loadInscripciones(showDetalle.id);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmAction(null);
      }
    });
  };

  const handleAnularPrueba = (insc) => {
    setConfirmAction({
      message: `¿Anular la prueba de ${insc.nombre_trabajador || 'este trabajador'}? Se borrará la nota (${insc.nota}%) y podrá rendir nuevamente sin consumir intentos.`,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/sesiones/${showDetalle.id}/inscripciones/${insc.id}/anular-prueba`, {}, { headers });
          toast.success('Prueba anulada. El trabajador puede volver a rendirla.');
          if (showDetalle) loadInscripciones(showDetalle.id);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al anular la prueba'); }
        setConfirmAction(null);
      }
    });
  };

  const loadInscripciones = async (sesionId) => {
    try {
      const res = await axios.get(`${API}/sesiones/${sesionId}/inscripciones`, { headers });
      setInscripciones(res.data);
    } catch { setInscripciones([]); }
  };

  const MIN_APROBACION = 85;

  // Hoy en zona Chile (yyyy-mm-dd) para identificar sesiones pasadas/cerradas
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
  const isPast = (s) => s?.fecha && s.fecha < todayStr;
  const isToday = (s) => s?.fecha === todayStr;

  // Orden Próximas Sesiones:
  //  1) Sesión(es) de HOY primero (destacadas)
  //  2) Sesiones futuras: la más cercana primero (ASC)
  //  3) Sesiones pasadas al final: la más reciente primero (DESC)
  //  Empatar por hora_inicio ASC en cada grupo.
  const proximasSesiones = React.useMemo(() => {
    const arr = [...(sesiones || [])];
    const bucket = (s) => (isToday(s) ? 0 : s.fecha > todayStr ? 1 : 2);
    return arr.sort((a, b) => {
      const ba = bucket(a), bb = bucket(b);
      if (ba !== bb) return ba - bb;
      const fa = a.fecha || '', fb = b.fecha || '';
      // Futuras ASC, pasadas DESC, hoy indistinto
      const cmp = ba === 2 ? fb.localeCompare(fa) : fa.localeCompare(fb);
      if (cmp !== 0) return cmp;
      return (a.hora_inicio || '').localeCompare(b.hora_inicio || '');
    });
  }, [sesiones, todayStr]);

  // Hora actual en Chile (HH:MM)
  const horaActualChile = () => new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Santiago', hour: '2-digit', minute: '2-digit', hour12: false });

  // ¿Las pruebas ya están cerradas para esta sesión? (mismo día de la sesión y hora >= cierre_prueba_hora)
  const pruebasCerradas = (s) => {
    if (!s?.fecha) return false;
    if (s.fecha < todayStr) return true;  // sesión pasada → cerradas
    if (s.fecha > todayStr) return false; // sesión futura → no aplica corte aún
    // mismo día → comparar hora actual vs cierre
    return horaActualChile() >= (cierrePruebaHora || '15:00');
  };

  const guardarEval = async (insc, cambios) => {
    const asistencia = cambios.asistencia !== undefined ? cambios.asistencia : (insc.asistencia || '');
    let nota = cambios.nota !== undefined ? cambios.nota : (insc.nota != null ? Number(insc.nota) : null);
    if (!asistencia) {
      toast.error('Marca Presente o Ausente antes');
      return;
    }
    if (asistencia === 'ausente') nota = null;
    let resultado = null;
    if (asistencia === 'presente') {
      if (nota != null && !Number.isNaN(nota)) {
        resultado = nota >= MIN_APROBACION ? 'aprobado' : 'reprobado';
      }
    } else {
      resultado = 'reprobado';
    }
    try {
      await axios.put(`${API}/sesiones/inscripcion/${insc.id}/resultado`,
        { asistencia, resultado, nota }, { headers });
      toast.success('Evaluación guardada');
      if (showDetalle) loadInscripciones(showDetalle.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al evaluar');
    }
  };

  const iniciarPrueba = async (insc) => {
    try {
      await axios.put(`${API}/sesiones/inscripcion/${insc.id}/iniciar-prueba`, {}, { headers });
      toast.success('Prueba iniciada. Ya puede registrar la nota.');
      if (showDetalle) loadInscripciones(showDetalle.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar prueba');
    }
  };

  const toggleWebcontrol = async (insc) => {
    const next = !insc.webcontrol;
    // Optimistic update
    setInscripciones(prev => prev.map(x => x.id === insc.id ? { ...x, webcontrol: next } : x));
    try {
      await axios.put(`${API}/sesiones/inscripcion/${insc.id}/webcontrol`, { webcontrol: next }, { headers });
    } catch (e) {
      // revertir
      setInscripciones(prev => prev.map(x => x.id === insc.id ? { ...x, webcontrol: !next } : x));
      toast.error(e.response?.data?.detail || 'No se pudo guardar WebControl');
    }
  };

  const openDetalle = (sesion) => {
    setShowDetalle(sesion);
    loadInscripciones(sesion.id);
  };

  // Inscribir workers flow
  const openInscribir = (sesion) => {
    setShowInscribir(sesion);
    setSelectedEmpresa('');
    setWorkers([]);
    setSelectedWorkers([]);
    setWorkerSearch('');
  };

  const loadWorkers = async (empresaId) => {
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras/${empresaId}/usuarios`, { headers });
      setWorkers(res.data);
    } catch { setWorkers([]); }
  };

  useEffect(() => {
    if (selectedEmpresa) loadWorkers(selectedEmpresa);
  }, [selectedEmpresa]);

  const toggleWorker = (w) => {
    const fullName = `${w.nombre || ''} ${w.apellido || ''}`.trim();
    setSelectedWorkers(prev => prev.find(s => s.rut === w.rut) ? prev.filter(s => s.rut !== w.rut) : [...prev, { rut: w.rut, nombre: fullName }]);
  };

  const handleInscribir = async (liberarPrevias = false) => {
    if (!selectedWorkers.length || !selectedEmpresa) return;
    try {
      const res = await axios.post(`${API}/sesiones/inscribir`, {
        sesion_id: showInscribir.id,
        empresa_id: parseInt(selectedEmpresa),
        trabajadores: selectedWorkers,
        liberar_previas: liberarPrevias,
      }, { headers });
      const liberadas = res.data?.liberadas || 0;
      toast.success(
        liberadas > 0
          ? `${res.data.inscritos} inscripto(s) · ${liberadas} liberado(s) de sesión previa`
          : `${res.data.inscritos} trabajador(es) inscrito(s)`
      );
      setShowInscribir(null);
      setConflictoInscribir(null);
      fetchSesiones();
      if (showDetalle) loadInscripciones(showDetalle.id);
    } catch (e) {
      // 409 = trabajadores ya inscritos en otra sesión del mismo curso → abrir modal de confirmación
      if (e.response?.status === 409 && e.response?.data?.detail?.code === 'worker_enrolled_in_other_session') {
        setConflictoInscribir(e.response.data.detail);
        return;
      }
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Error al inscribir');
    }
  };

  // Calendar helpers
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDay = (firstDay.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);

  const getSesionesForDay = (d) => {
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return sesiones.filter(s => s.fecha === dateStr);
  };

  const filteredWorkers = workers.filter(w => {
    const q = workerSearch.toLowerCase();
    if (!q) return true;
    return w.nombre?.toLowerCase().includes(q)
      || w.apellido?.toLowerCase().includes(q)
      || w.cargo?.toLowerCase().includes(q)
      || w.rut?.includes(q);
  });

  return (
    <div className="space-y-6" data-testid="capacitador-calendario">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Calendario de Sesiones</h1>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          data-testid="create-session-btn">
          <Plus className="w-4 h-4" />Nueva Sesion
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <button onClick={() => setCurrentMonth(new Date(year, month-1))} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-slate-800">{MONTHS[month]} {year}</h2>
          <button onClick={() => setCurrentMonth(new Date(year, month+1))} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-7">
          {DAYS.map(d => <div key={d} className="text-center text-xs font-semibold text-slate-500 py-2 border-b border-slate-100">{d}</div>)}
          {days.map((d, i) => {
            const daySesiones = d ? getSesionesForDay(d) : [];
            const isTodayCell = d && new Date().getDate() === d && new Date().getMonth() === month && new Date().getFullYear() === year;
            const maxShow = 2;
            const dateStr = d ? `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}` : '';
            return (
              <div key={i} className={`min-h-[90px] border-b border-r border-slate-100 p-1 ${d ? 'bg-white' : 'bg-slate-50/50'}`}>
                {d && (
                  <>
                    <span className={`text-xs font-medium ${isTodayCell ? 'bg-teal-500 text-white px-1.5 py-0.5 rounded-full' : 'text-slate-500'}`}>{d}</span>
                    <div className="mt-1 space-y-0.5">
                      {daySesiones.slice(0, maxShow).map(s => {
                        const past = isPast(s);
                        return (
                        <div key={s.id} onClick={() => openDetalle(s)}
                          className={`text-[10px] px-1.5 py-0.5 rounded cursor-pointer truncate ${
                            past ? 'bg-slate-200 text-slate-500 line-through hover:bg-slate-300'
                            : s.cupo_disponible === 0 ? 'bg-red-100 text-red-700'
                            : 'bg-teal-100 text-teal-700 hover:bg-teal-200'}`}
                          title={`${s.capacitacion_nombre} (${s.inscritos}/${fmtCupo(s.cupo_maximo)})${past ? ' - Cerrada' : ''}`}>
                          {s.capacitacion_nombre?.substring(0,18)} {s.inscritos}/{fmtCupo(s.cupo_maximo)}
                        </div>
                        );
                      })}
                      {daySesiones.length > maxShow && (
                        <button
                          type="button"
                          data-testid={`more-sessions-btn-${dateStr}`}
                          onClick={(e) => { e.stopPropagation(); setMoreDayModal({ dateStr, sesiones: daySesiones }); }}
                          className="w-full text-left text-[10px] text-teal-600 font-semibold px-1.5 py-0.5 rounded hover:bg-teal-100 hover:text-teal-800 transition-colors cursor-pointer"
                        >
                          +{daySesiones.length - maxShow} mas
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Proximas Sesiones */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Proximas Sesiones</h3>
          {(() => {
            const cerradasCount = proximasSesiones.filter(s => isPast(s)).length;
            if (cerradasCount === 0) return null;
            return (
              <button onClick={() => setShowCerradas(v => !v)}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-md px-2 py-1 transition-colors"
                data-testid="toggle-cerradas-btn">
                {showCerradas ? 'Ocultar cerradas' : `Ver todas las cerradas (${cerradasCount})`}
              </button>
            );
          })()}
        </div>
        {(() => {
          const visibleSesiones = showCerradas ? proximasSesiones : proximasSesiones.filter(s => !isPast(s));
          if (visibleSesiones.length === 0) {
            return <p className="text-sm text-slate-400">No hay sesiones programadas</p>;
          }
          return (
          <div className="space-y-2">
            {visibleSesiones.map(s => {
              const past = isPast(s);
              const today = isToday(s);
              return (
              <div key={s.id} onClick={() => openDetalle(s)}
                data-testid={`proxima-sesion-${s.id}`}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  today ? 'border-teal-400 bg-teal-50 ring-2 ring-teal-300 shadow-sm hover:bg-teal-100'
                  : past ? 'border-slate-200 bg-slate-50 opacity-70 hover:bg-slate-100'
                  : 'border-slate-100 hover:border-teal-200 hover:bg-teal-50/30'}`}>
                <div>
                  <p className={`font-medium ${past ? 'text-slate-500' : 'text-slate-800'}`}>
                    {toTitleCase(s.capacitacion_nombre)}
                    {today && <span className="ml-2 text-[10px] font-bold uppercase bg-teal-600 text-white px-1.5 py-0.5 rounded" data-testid="badge-hoy">Hoy</span>}
                    {past && <span className="ml-2 text-[10px] font-bold uppercase bg-slate-300 text-slate-700 px-1.5 py-0.5 rounded">Cerrada</span>}
                  </p>
                  <p className="text-xs text-slate-500">{formatDDMMYYYY(s.fecha)} | {s.hora_inicio} - {s.hora_fin} | {s.lugar}
                    {s.url_sesion && (
                      <a href={s.url_sesion} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="ml-2 text-teal-600 hover:text-teal-800 inline-flex items-center gap-0.5">
                        <Video className="w-3 h-3" /><ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${s.cupo_disponible === 0 ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}`}>
                    <Users className="w-3 h-3 inline mr-1" />{s.inscritos}/{fmtCupo(s.cupo_maximo)}
                  </span>
                  {!past && (
                    <button onClick={(e) => { e.stopPropagation(); openInscribir(s); }}
                      className="text-xs px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                      data-testid={`inscribir-btn-${s.id}`}>
                      <Plus className="w-3 h-3 inline mr-1" />Inscribir
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
          );
        })()}
      </div>

      {/* Modal ver todas las sesiones del día */}
      {moreDayModal && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setMoreDayModal(null)} data-testid="more-sessions-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-base font-bold text-slate-900">Sesiones del dia</h2>
                <p className="text-xs text-slate-500">{formatDDMMYYYY(moreDayModal.dateStr)} &middot; {moreDayModal.sesiones.length} sesion(es)</p>
              </div>
              <button onClick={() => setMoreDayModal(null)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="close-more-sessions-modal"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {moreDayModal.sesiones.map(s => (
                <button
                  key={s.id}
                  data-testid={`day-session-${s.id}`}
                  onClick={() => { openDetalle(s); setMoreDayModal(null); }}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:bg-teal-50/40 hover:border-teal-200 text-left transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">{toTitleCase(s.capacitacion_nombre)}</p>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500">
                      {(s.hora_inicio || s.hora_fin) && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.hora_inicio}{s.hora_fin ? ` - ${s.hora_fin}` : ''}</span>}
                      {s.lugar && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{s.lugar}</span>}
                    </div>
                  </div>
                  <span className={`shrink-0 text-[11px] font-medium px-2 py-1 rounded-full ${s.cupo_disponible === 0 ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}`}>
                    <Users className="w-3 h-3 inline mr-1" />{s.inscritos}/{fmtCupo(s.cupo_maximo)}
                  </span>
                </button>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-between items-center">
              <p className="text-[11px] text-slate-400">Haz click en una sesion para ver detalles</p>
              <button onClick={() => setMoreDayModal(null)} className="px-4 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cerrar</button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Crear Sesion */}
      {showModal && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Nueva Sesion</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Capacitacion</label>
                <select value={form.capacitacion_id} onChange={e => setForm({...form, capacitacion_id: e.target.value})}
                  required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="session-course-select">
                  <option value="">Seleccionar curso</option>
                  {capacitaciones.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Inicio</label>
                  <input type="time" value={form.hora_inicio} onChange={e => setForm({...form, hora_inicio: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fin</label>
                  <input type="time" value={form.hora_fin} onChange={e => setForm({...form, hora_fin: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              {/* Selector múltiple de fechas — hasta 30 días de rango entre la primera y la última */}
              <FechasMultiples
                base={form.fecha}
                extras={form.fechas_extra || []}
                onBase={v => setForm(f => ({...f, fecha: v}))}
                onExtras={arr => setForm(f => ({...f, fechas_extra: arr}))}
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cupo Maximo</label>
                  <input type="number" value={form.cupo_maximo} onChange={e => setForm({...form, cupo_maximo: parseInt(e.target.value)||50})} min={1} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Lugar</label>
                  <input type="text" value={form.lugar} onChange={e => setForm({...form, lugar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL Teams / Meet</label>
                <input type="url" value={form.url_sesion || ''} onChange={e => setForm({...form, url_sesion: e.target.value})}
                  placeholder="https://teams.microsoft.com/..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                <textarea value={form.notas || ''} onChange={e => setForm({...form, notas: e.target.value})} rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none" />
              </div>
              <button type="submit" className="w-full py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium" data-testid="submit-session-btn">Crear Sesion</button>
            </form>
          </div>
        </div>
      , document.body)}

      {/* Modal Detalle */}
      {showDetalle && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowDetalle(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[1400px] max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="session-detail-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{toTitleCase(showDetalle.capacitacion_nombre)}</h2>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{formatDDMMYYYY(showDetalle.fecha)} | {showDetalle.hora_inicio} - {showDetalle.hora_fin}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{showDetalle.lugar}</span>
                  {showDetalle.url_sesion && (
                    <a href={showDetalle.url_sesion} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-teal-600 hover:text-teal-800 hover:underline">
                      <Video className="w-3.5 h-3.5" />Link<ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isPast(showDetalle) && (
                  <button onClick={() => openQrModal(showDetalle)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs hover:bg-slate-900"
                    data-testid="compartir-prueba-btn"
                    title="Compartir URL y QR de la prueba con los trabajadores">
                    <QrCode className="w-3.5 h-3.5" />Prueba QR
                  </button>
                )}
                {!isPast(showDetalle) && pruebaEstado && (
                  <div className="flex items-center gap-0.5 border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="prueba-toggle-group"
                    title={pruebaEstado.motivo}>
                    <button onClick={() => togglePrueba(showDetalle.id, 'on')}
                      disabled={togglingPrueba}
                      className={`flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium transition-colors ${
                        pruebaEstado.modo === 'manual_on' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-emerald-50'
                      } disabled:opacity-60`}
                      data-testid="btn-prueba-on"
                      title="Activar prueba manualmente">
                      <Power className="w-3 h-3" />On
                    </button>
                    <button onClick={() => togglePrueba(showDetalle.id, 'auto')}
                      disabled={togglingPrueba}
                      className={`flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium border-x border-slate-200 transition-colors ${
                        pruebaEstado.modo === 'auto' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-teal-50'
                      } disabled:opacity-60`}
                      data-testid="btn-prueba-auto"
                      title={`Modo automático (${pruebaEstado.ventana?.inicio}–${pruebaEstado.ventana?.fin} hrs del día de la sesión)`}>
                      <Zap className="w-3 h-3" />Auto
                    </button>
                    <button onClick={() => togglePrueba(showDetalle.id, 'off')}
                      disabled={togglingPrueba}
                      className={`flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium transition-colors ${
                        pruebaEstado.modo === 'manual_off' ? 'bg-rose-600 text-white' : 'text-slate-600 hover:bg-rose-50'
                      } disabled:opacity-60`}
                      data-testid="btn-prueba-off"
                      title="Desactivar prueba manualmente">
                      <PowerOff className="w-3 h-3" />Off
                    </button>
                  </div>
                )}
                {!isPast(showDetalle) && showDetalle.estado !== 'cerrada' && (
                  <button onClick={() => openInscribir(showDetalle)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs hover:bg-teal-700">
                    <Plus className="w-3 h-3" />Inscribir
                  </button>
                )}
                {!isPast(showDetalle) && (showDetalle.inscritos || 0) > 0 && (
                  <button onClick={() => setCloseModal({ sesion: showDetalle })}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs ${
                      showDetalle.estado === 'cerrada'
                        ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        : 'bg-amber-600 text-white hover:bg-amber-700'
                    }`}
                    title={showDetalle.estado === 'cerrada' ? 'Reenviar correos' : 'Cerrar nómina y notificar a inscritos'}
                    data-testid="btn-cerrar-nomina">
                    {showDetalle.estado === 'cerrada' ? <><Mail className="w-3.5 h-3.5" />Reenviar correos</> : <><Lock className="w-3.5 h-3.5" />Cerrar Nómina</>}
                  </button>
                )}
                {isPast(showDetalle) && (
                  <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-700 px-2 py-1 rounded">Sesión Cerrada</span>
                )}
                {showDetalle.estado === 'cerrada' && !isPast(showDetalle) && (
                  <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-2 py-1 rounded" title="Nómina cerrada">Nómina Cerrada</span>
                )}
                <button onClick={() => openEdit(showDetalle)}
                  className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg" title="Editar sesión" data-testid="btn-editar-sesion">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(showDetalle.id)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="relative">
                  <button onClick={() => setShowAyuda(v => !v)}
                    className={`p-1.5 rounded-lg transition-colors ${showAyuda ? 'bg-teal-100 text-teal-700' : 'text-slate-500 hover:bg-slate-100'}`}
                    title="Guía rápida para el Capacitador"
                    data-testid="btn-ayuda-capacitador">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                  {showAyuda && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowAyuda(false)} />
                      <div className="absolute right-0 top-full mt-2 z-50 w-[320px] bg-white border border-slate-200 rounded-xl shadow-2xl p-3" data-testid="ayuda-capacitador-panel">
                        <div className="flex items-start justify-between gap-2 mb-2 pb-2 border-b border-slate-100">
                          <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded-md bg-teal-600 text-white flex items-center justify-center">
                              <HelpCircle className="w-3 h-3" />
                            </div>
                            <p className="text-[12px] font-bold text-slate-900">Guía rápida</p>
                          </div>
                          <button onClick={() => setShowAyuda(false)} className="p-0.5 hover:bg-slate-100 rounded" data-testid="cerrar-ayuda">
                            <X className="w-3 h-3 text-slate-400" />
                          </button>
                        </div>
                        <ol className="space-y-1.5">
                          <li className="flex items-start gap-1.5">
                            <span className="w-4 h-4 rounded-full bg-teal-600 text-white text-[9px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                            <p className="text-[11px] leading-snug text-slate-600">
                              <strong className="text-slate-800">Completa la asistencia</strong> · marca Presente/Ausente.
                            </p>
                          </li>
                          <li className="flex items-start gap-1.5">
                            <span className="w-4 h-4 rounded-full bg-teal-600 text-white text-[9px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                            <p className="text-[11px] leading-snug text-slate-600">
                              <strong className="text-slate-800">Activa la prueba</strong> · se abre auto <strong>13:00–15:00</strong>. Si el horario es distinto, usa <span className="inline-flex items-center gap-0.5 px-1 rounded bg-emerald-100 text-emerald-700 font-semibold"><Power className="w-2.5 h-2.5" />On</span> y <span className="inline-flex items-center gap-0.5 px-1 rounded bg-rose-100 text-rose-700 font-semibold"><PowerOff className="w-2.5 h-2.5" />Off</span> al terminar.
                            </p>
                          </li>
                          <li className="flex items-start gap-1.5">
                            <span className="w-4 h-4 rounded-full bg-teal-600 text-white text-[9px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                            <p className="text-[11px] leading-snug text-slate-600">
                              <strong className="text-slate-800">Comparte el QR o Link</strong> · botón <em>Prueba QR</em>. Los trabajadores ingresan su RUT.
                            </p>
                          </li>
                          <li className="flex items-start gap-1.5">
                            <span className="w-4 h-4 rounded-full bg-teal-600 text-white text-[9px] font-bold flex items-center justify-center shrink-0 mt-0.5">4</span>
                            <p className="text-[11px] leading-snug text-slate-600">
                              <strong className="text-slate-800">Espera los resultados</strong> · encuesta y prueba llegan a esta tabla. Cierra la nómina al final.
                            </p>
                          </li>
                        </ol>
                      </div>
                    </>
                  )}
                </div>
                <button onClick={() => setShowDetalle(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="flex items-center gap-4 mb-4">
                <span className={`text-sm font-medium px-3 py-1 rounded-full ${showDetalle.cupo_disponible === 0 ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}`}>
                  {showDetalle.inscritos}/{fmtCupo(showDetalle.cupo_maximo)} inscritos
                </span>
                <span className="text-xs text-slate-400">{showDetalle.cupo_disponible} cupos disponibles</span>
              </div>

              {/* Banner: Pruebas cerradas por hora */}
              {pruebasCerradas(showDetalle) && !isPast(showDetalle) && (
                <div className="mb-4 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 flex items-start gap-2" data-testid="pruebas-cerradas-banner">
                  <Clock className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-900 leading-snug">
                    <strong>Pruebas cerradas</strong> (regla: corte {cierrePruebaHora} hrs del día de la sesión). Como Capacitador puedes seguir registrando resultados excepcionales, pero verifica la justificación.
                  </p>
                </div>
              )}
              {inscripciones.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">RUT</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Nombre</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Empresa</th>
                      <th className="text-center px-3 py-2 font-semibold text-slate-600">Asistencia</th>
                      <th className="text-center px-3 py-2 font-semibold text-slate-600">Nota %</th>
                      <th className="text-center px-3 py-2 font-semibold text-slate-600">Resultado</th>
                      <th className="text-center px-3 py-2 font-semibold text-slate-600">WebControl</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inscripciones.map((insc, i) => (
                      <tr key={insc.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`insc-row-${insc.id}`}>
                        <td className="px-3 py-2 text-slate-400">{i+1}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600 whitespace-nowrap">{formatRut(insc.rut_trabajador)}</td>
                        <td className="px-3 py-2 text-slate-800 whitespace-nowrap">{toTitleCase(insc.nombre_trabajador)}</td>
                        <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{toTitleCase(insc.empresa_nombre)}</td>
                        <td className="px-3 py-2 text-center">
                          <select value={insc.asistencia || ''}
                            onChange={e => guardarEval(insc, { asistencia: e.target.value })}
                            data-testid={`asist-${insc.id}`}
                            className={`text-[11px] font-semibold border rounded px-2 py-1 ${
                              insc.asistencia === 'presente' ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                              : insc.asistencia === 'ausente' ? 'bg-red-50 text-red-700 border-red-300'
                              : 'bg-white text-slate-500 border-slate-200'
                            }`}>
                            <option value="">—</option>
                            <option value="presente">Presente</option>
                            <option value="ausente">Ausente</option>
                          </select>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {insc.asistencia === 'presente' && !insc.prueba_iniciada ? (
                            <button
                              type="button"
                              onClick={() => iniciarPrueba(insc)}
                              data-testid={`iniciar-prueba-${insc.id}`}
                              className="text-[10px] font-semibold px-2.5 py-1 bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300 rounded transition-colors whitespace-nowrap"
                              title="Iniciar la prueba para habilitar el ingreso de nota">
                              Iniciar Prueba
                            </button>
                          ) : insc.nota != null ? (
                            <span
                              data-testid={`nota-locked-${insc.id}`}
                              title="Nota registrada (1 intento por sesión). Para corregir, marque Ausente y cree una nueva sesión."
                              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border rounded ${
                                insc.resultado === 'aprobado'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : 'bg-red-50 text-red-700 border-red-200'
                              }`}>
                              {Number(insc.nota).toFixed(0)}
                              <span className="text-[9px] opacity-60">🔒</span>
                            </span>
                          ) : (
                            <input type="number" min="0" max="100"
                              defaultValue=""
                              disabled={insc.asistencia !== 'presente' || !insc.prueba_iniciada}
                              data-testid={`nota-${insc.id}`}
                              onBlur={e => {
                                const n = e.target.value === '' ? null : parseFloat(e.target.value);
                                guardarEval(insc, { nota: n });
                              }}
                              className="text-[11px] w-16 text-center border border-slate-200 rounded px-1 py-1 disabled:bg-slate-100 disabled:text-slate-400" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {insc.resultado ? (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              insc.resultado === 'aprobado' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {insc.resultado === 'aprobado' ? 'Aprobado' : 'Reprobado'}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <label className="inline-flex items-center justify-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!insc.webcontrol}
                              onChange={() => toggleWebcontrol(insc)}
                              data-testid={`webcontrol-${insc.id}`}
                              className="w-5 h-5 rounded accent-teal-600 cursor-pointer"
                              title={insc.webcontrol ? 'Marcado en WebControl' : 'Marcar como cargado en WebControl'}
                            />
                          </label>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {insc.nota != null && (
                            <button onClick={() => handleAnularPrueba(insc)}
                              data-testid={`anular-prueba-${insc.id}`}
                              className="text-xs text-amber-600 hover:underline mr-2"
                              title="Anula la nota y permite volver a rendir sin consumir intentos">Anular prueba</button>
                          )}
                          <button onClick={() => handleCancelInscripcion(insc.id)}
                            data-testid={`cancel-${insc.id}`}
                            className="text-xs text-red-500 hover:underline">Cancelar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">No hay trabajadores inscritos aun</p>
              )}
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Inscribir */}
      {showInscribir && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowInscribir(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Inscribir Trabajadores</h2>
                <p className="text-sm text-slate-500">{toTitleCase(showInscribir.capacitacion_nombre)} - {formatDDMMYYYY(showInscribir.fecha)}</p>
                <p className="text-xs text-slate-400">{showInscribir.cupo_disponible} cupos disponibles</p>
              </div>
              <button onClick={() => setShowInscribir(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Empresa Colaboradora</label>
                <select value={selectedEmpresa} onChange={e => { setSelectedEmpresa(e.target.value); setSelectedWorkers([]); }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Seleccionar empresa</option>
                  {empresas.map(e => <option key={e.id} value={e.id}>{toTitleCase(e.razon_social)}</option>)}
                </select>
              </div>
              {selectedEmpresa && (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <input type="text" value={workerSearch} onChange={e => setWorkerSearch(e.target.value)}
                      placeholder="Buscar por nombre o RUT..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </div>
                  <div className="max-h-[300px] overflow-y-auto border border-slate-200 rounded-lg">
                    {filteredWorkers.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Sin trabajadores</p>
                    ) : filteredWorkers.map(w => {
                      const sel = selectedWorkers.find(s => s.rut === w.rut);
                      return (
                        <label key={w.rut} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0">
                          <input type="checkbox" checked={!!sel} onChange={() => toggleWorker(w)} className="rounded border-slate-300 text-teal-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800 truncate">
                              {toTitleCase(`${w.nombre || ''} ${w.apellido || ''}`.trim())}
                            </p>
                            <p className="text-xs text-slate-400 font-mono">
                              {formatRut(w.rut)}
                              {w.cargo ? <span className="ml-2 text-slate-500 font-sans normal-case">· {toTitleCase(w.cargo)}</span> : null}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            {selectedWorkers.length > 0 && (
              <div className="px-6 py-4 border-t border-slate-200">
                <button onClick={() => handleInscribir(false)}
                  className="w-full py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium">
                  Inscribir {selectedWorkers.length} trabajador(es)
                </button>
              </div>
            )}
          </div>
        </div>
      , document.body)}

      {/* Modal Conflicto: trabajador ya inscrito en otra sesión del mismo curso */}
      {conflictoInscribir && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="modal-conflicto-inscribir">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="text-base font-semibold text-slate-900">Trabajador ya inscrito en otra sesión</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600">{conflictoInscribir.message}</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {conflictoInscribir.conflictos?.map((c) => (
                  <div key={c.inscripcion_id} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <p className="text-sm font-medium text-slate-800">{c.nombre || c.rut}</p>
                    <p className="text-xs text-slate-500 font-mono">{c.rut}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-slate-600">Sesión del <strong>{c.fecha_sesion ? c.fecha_sesion.split('-').reverse().join('-') : '—'}</strong></span>
                      {c.nomina_cerrada && <span className="inline-flex items-center px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">Nómina cerrada</span>}
                      {c.sesion_estado === 'cerrada' && !c.nomina_cerrada && <span className="inline-flex items-center px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full font-semibold">Cerrada</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 text-xs text-teal-900">
                <strong>Al confirmar</strong>, se cancelará la inscripción previa y se procederá a inscribir al trabajador en esta nueva sesión.
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setConflictoInscribir(null)}
                className="px-4 h-9 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                data-testid="btn-conflicto-cancelar"
              >Cancelar</button>
              <button
                onClick={() => handleInscribir(true)}
                className="px-4 h-9 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 inline-flex items-center gap-1.5"
                data-testid="btn-conflicto-liberar"
              >
                Liberar previa e inscribir
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Confirmacion */}
      {confirmAction && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="confirm-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-sm text-slate-700">{confirmAction.message}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Cancelar</button>
              <button onClick={confirmAction.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">Confirmar</button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal QR Prueba Pública */}
      {qrModal && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setQrModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="qr-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><QrCode className="w-5 h-5 text-slate-700" />Prueba para Trabajadores</h3>
              <button onClick={() => setQrModal(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-6 py-5">
              <p className="text-xs text-slate-600 mb-1">Sesión: <strong>{toTitleCase(qrModal.sesion.capacitacion_nombre)}</strong></p>
              <p className="text-xs text-slate-500 mb-3">{formatDDMMYYYY(qrModal.sesion.fecha)} · {qrModal.sesion.hora_inicio} - {qrModal.sesion.hora_fin}</p>

              {!qrModal.loading && qrModal.prueba_modo && (
                <div className={`mb-4 px-3 py-2 rounded-lg border flex items-start gap-2 ${
                  qrModal.prueba_activa
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`} data-testid="qr-prueba-status">
                  {qrModal.prueba_activa ? <Power className="w-4 h-4 mt-0.5 shrink-0" /> : <PowerOff className="w-4 h-4 mt-0.5 shrink-0" />}
                  <div className="text-[11px] leading-snug">
                    <p className="font-semibold">{qrModal.prueba_activa ? 'Prueba ACTIVA' : 'Prueba INACTIVA'} · Modo {qrModal.prueba_modo === 'auto' ? 'automático' : qrModal.prueba_modo === 'manual_on' ? 'manual (ON)' : 'manual (OFF)'}</p>
                    <p>{qrModal.prueba_motivo}</p>
                  </div>
                </div>
              )}

              {qrModal.loading ? (
                <div className="flex items-center justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700" /></div>
              ) : (
                <>
                  <div className="flex flex-col items-center bg-slate-50 rounded-xl p-4 mb-4">
                    <img src={qrModal.qr_data_url} alt="QR de la prueba" className="w-56 h-56 mb-2" data-testid="qr-image" />
                    <p className="text-[11px] text-slate-500 text-center max-w-xs">Los trabajadores escanean este QR e ingresan su RUT para rendir la prueba.</p>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-white p-2 mb-3">
                    <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide mb-1 px-1">Link directo</p>
                    <div className="flex items-center gap-1.5">
                      <Link2 className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
                      <span className="text-xs font-mono text-slate-700 truncate flex-1" title={qrModal.url}>{qrModal.url}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => copyToClipboard(qrModal.url)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-800 text-white text-xs font-medium rounded-md hover:bg-slate-900"
                      data-testid="copy-prueba-url-btn">
                      <Copy className="w-3.5 h-3.5" />Copiar link
                    </button>
                    <button onClick={() => downloadQr(qrModal.qr_data_url, qrModal.sesion.capacitacion_nombre)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-100"
                      data-testid="download-qr-btn">
                      <QrCode className="w-3.5 h-3.5" />Descargar QR
                    </button>
                    <a href={qrModal.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-100"
                      data-testid="open-prueba-btn">
                      <Share2 className="w-3.5 h-3.5" />Abrir
                    </a>
                  </div>

                  <p className="text-[10px] text-slate-400 mt-3 leading-snug">
                    Validación: RUT debe estar en la lista de inscritos confirmados. Cada trabajador tiene 1 intento por sesión.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      , document.body)}

      {/* Editar sesión */}
      {editModal && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">Editar sesión</h3>
              <button onClick={() => setEditModal(null)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="btn-cerrar-edit">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Fecha</label>
                  <input type="date" value={editModal.form.fecha}
                    onChange={e => setEditModal(m => ({ ...m, form: {...m.form, fecha: e.target.value} }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-fecha" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Cupo máximo</label>
                  <input type="number" min={1} value={editModal.form.cupo_maximo}
                    onChange={e => setEditModal(m => ({ ...m, form: {...m.form, cupo_maximo: parseInt(e.target.value)||1} }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-cupo" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Inicio</label>
                  <input type="time" value={editModal.form.hora_inicio}
                    onChange={e => setEditModal(m => ({ ...m, form: {...m.form, hora_inicio: e.target.value} }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Fin</label>
                  <input type="time" value={editModal.form.hora_fin}
                    onChange={e => setEditModal(m => ({ ...m, form: {...m.form, hora_fin: e.target.value} }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Lugar</label>
                <input value={editModal.form.lugar}
                  onChange={e => setEditModal(m => ({ ...m, form: {...m.form, lugar: e.target.value} }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">URL Teams / Meet</label>
                <input value={editModal.form.url_sesion}
                  onChange={e => setEditModal(m => ({ ...m, form: {...m.form, url_sesion: e.target.value} }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="https://teams.microsoft.com/..." />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Notas</label>
                <textarea value={editModal.form.notas} rows={2}
                  onChange={e => setEditModal(m => ({ ...m, form: {...m.form, notas: e.target.value} }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={() => setEditModal(null)} className="px-4 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 text-slate-700">Cancelar</button>
              <button
                onClick={() => setScopeModal({ form: editModal.form })}
                className="px-4 py-2 rounded-lg text-sm bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                data-testid="btn-guardar-edit">
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Confirmación de alcance */}
      {scopeModal && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.6)"}} className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 mb-2">¿A qué sesiones aplicar los cambios?</h3>
            <p className="text-xs text-slate-500 mb-5">
              Puedes editar sólo la sesión seleccionada, o replicar los cambios a esta y todas las sesiones futuras de la misma capacitación (mismo curso, fecha posterior). La fecha nueva se aplica sólo a esta sesión.
            </p>
            <div className="space-y-2">
              <button
                onClick={() => submitEdit(scopeModal.form, 'single')}
                className="w-full text-left px-4 py-3 rounded-xl border border-slate-200 hover:border-teal-400 hover:bg-teal-50"
                data-testid="btn-scope-single">
                <p className="font-semibold text-slate-800 text-sm">Sólo esta sesión</p>
                <p className="text-xs text-slate-500 mt-0.5">Los cambios se guardan únicamente en la sesión seleccionada.</p>
              </button>
              <button
                onClick={() => submitEdit(scopeModal.form, 'future')}
                className="w-full text-left px-4 py-3 rounded-xl border border-slate-200 hover:border-teal-400 hover:bg-teal-50"
                data-testid="btn-scope-future">
                <p className="font-semibold text-slate-800 text-sm">Esta y todas las futuras</p>
                <p className="text-xs text-slate-500 mt-0.5">Se replicarán hora, cupo, lugar, URL y notas a todas las sesiones del mismo curso con fecha posterior.</p>
              </button>
            </div>
            <div className="flex justify-end mt-5">
              <button onClick={() => setScopeModal(null)} className="px-4 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 text-slate-700">Cancelar</button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Confirmar Cerrar Nómina */}
      {closeModal && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => !closingBusy && setCloseModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="modal-cerrar-nomina">
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                closeModal.sesion.estado === 'cerrada' ? 'bg-blue-50' : 'bg-amber-50'
              }`}>
                {closeModal.sesion.estado === 'cerrada'
                  ? <Mail className="w-5 h-5 text-blue-600" />
                  : <Lock className="w-5 h-5 text-amber-600" />}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">
                  {closeModal.sesion.estado === 'cerrada' ? 'Reenviar Notificación' : 'Cerrar Nómina'}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {closeModal.sesion.estado === 'cerrada'
                    ? 'Se reenviará el correo con los datos de la sesión a todos los inscritos con email registrado.'
                    : <>Se cerrará la nómina de <strong>{closeModal.sesion.capacitacion_nombre}</strong>, no se podrán inscribir más trabajadores y se notificará por email a los {closeModal.sesion.inscritos || 0} inscrito(s) con la fecha, hora, modalidad y enlace de la sesión.</>}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" disabled={closingBusy} onClick={() => setCloseModal(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                Cancelar
              </button>
              <button type="button" disabled={closingBusy || previewLoading}
                onClick={() => openPreview(closeModal.sesion)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                data-testid="btn-preview-email"
                title="Ver el HTML del correo con los datos reales antes de enviar">
                {previewLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Cargando…</> : <>👁 Vista previa</>}
              </button>
              <button type="button" disabled={closingBusy} onClick={handleCerrarNomina}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-lg font-semibold disabled:opacity-50 ${
                  closeModal.sesion.estado === 'cerrada' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'
                }`}
                data-testid="confirmar-cerrar-nomina">
                {closingBusy
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Enviando...</>
                  : closeModal.sesion.estado === 'cerrada'
                    ? <><Mail className="w-4 h-4" />Reenviar</>
                    : <><Lock className="w-4 h-4" />Cerrar y Notificar</>}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Preview del Email */}
      {previewData && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          onClick={() => setPreviewData(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()} data-testid="modal-preview-email">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Vista previa del correo</h3>
                <p className="text-xs text-slate-500 mt-1">
                  De: <span className="font-mono">{previewData.sender}</span> · Asunto: <span className="italic">{previewData.subject}</span>
                </p>
              </div>
              <button onClick={() => setPreviewData(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 text-xs text-slate-700 flex flex-wrap gap-3 items-center">
              <span><strong>{previewData.total}</strong> inscritos</span>
              <span className="text-emerald-700"><strong>{previewData.con_email}</strong> con email</span>
              {previewData.sin_email > 0 && <span className="text-red-600"><strong>{previewData.sin_email}</strong> sin email (no recibirán)</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 flex-1 overflow-hidden">
              <div className="md:col-span-2 overflow-y-auto bg-slate-100 p-3 md:p-4 min-h-[240px]">
                <iframe title="Preview email" srcDoc={previewData.html}
                  className="w-full h-full min-h-[420px] md:min-h-[500px] bg-white rounded-lg border border-slate-200 shadow-sm" />
              </div>
              <div className="md:col-span-1 border-t md:border-t-0 md:border-l border-slate-200 overflow-y-auto max-h-[240px] md:max-h-none">
                <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
                  Destinatarios ({previewData.destinatarios.length})
                </div>
                <ul className="text-xs divide-y divide-slate-100">
                  {previewData.destinatarios.map((d, i) => (
                    <li key={i} className={`px-3 py-2 ${!d.tiene_email ? 'bg-red-50' : ''}`}>
                      <div className="font-medium text-slate-800">{d.nombre || <span className="text-slate-300">—</span>}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{d.rut}</div>
                      {d.tiene_email
                        ? <div className="text-[11px] text-slate-600 truncate">{d.email}</div>
                        : <div className="text-[10px] text-red-600 font-semibold">Sin email — no recibirá</div>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button type="button" onClick={() => setPreviewData(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">
                Cerrar preview
              </button>
              {closeModal && (
                <button type="button" disabled={closingBusy}
                  onClick={() => { setPreviewData(null); handleCerrarNomina(); }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold disabled:opacity-50"
                  data-testid="preview-confirmar-envio">
                  <Lock className="w-4 h-4" />Confirmar y enviar
                </button>
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}

/** Selector de múltiples fechas con máximo 30 días de rango entre la primera y la última.
 *  El input `date` propio queda como fecha "base" (requerida). Al pulsar "+ Añadir día"
 *  aparece un nuevo input con min/max derivados de la base ± 30 días. Cada fecha extra
 *  se puede quitar. */
function FechasMultiples({ base, extras, onBase, onExtras }) {
  const [nueva, setNueva] = React.useState('');
  const ordenadas = React.useMemo(
    () => Array.from(new Set([base, ...extras].filter(Boolean))).sort(),
    [base, extras],
  );
  const rango = React.useMemo(() => {
    if (!base) return { min: '', max: '' };
    const d0 = new Date(base + 'T00:00');
    const dmax = new Date(d0); dmax.setDate(dmax.getDate() + 30);
    const dmin = new Date(d0); dmin.setDate(dmin.getDate() - 30);
    return { min: dmin.toISOString().slice(0, 10), max: dmax.toISOString().slice(0, 10) };
  }, [base]);
  const agregar = () => {
    if (!nueva) return;
    if (!base) { onBase(nueva); setNueva(''); return; }
    if (nueva === base || extras.includes(nueva)) { toast.error('Esa fecha ya está agregada'); return; }
    // Rango 30 días
    const min = ordenadas[0];
    const max = ordenadas[ordenadas.length - 1];
    const candidato = new Date(nueva + 'T00:00');
    const d0 = new Date(min + 'T00:00');
    const d1 = new Date(max + 'T00:00');
    const nMin = candidato < d0 ? candidato : d0;
    const nMax = candidato > d1 ? candidato : d1;
    if ((nMax - nMin) / 86400000 > 30) {
      toast.error('El rango entre la primera y la última fecha no puede superar 30 días');
      return;
    }
    onExtras([...extras, nueva]);
    setNueva('');
  };
  const quitar = (f) => {
    if (f === base) {
      // Si quitamos la base, promovemos la siguiente extra a base
      const rest = extras.filter(x => x !== base);
      onBase(rest[0] || '');
      onExtras(rest.slice(1));
    } else {
      onExtras(extras.filter(x => x !== f));
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-slate-700">Fecha(s) *</label>
        <span className="text-[10px] text-slate-400">Máx 30 días de rango · replica todos los campos</span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <input type="date" value={nueva} onChange={e => setNueva(e.target.value)}
          min={base ? rango.min : undefined} max={base ? rango.max : undefined}
          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="input-nueva-fecha" />
        <button type="button" onClick={agregar}
          className="px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium whitespace-nowrap"
          data-testid="btn-add-fecha">
          + Añadir día
        </button>
      </div>
      {ordenadas.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {ordenadas.map(f => (
            <span key={f} className={`inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-[11px] font-semibold ${f === base ? 'bg-teal-100 text-teal-800 border border-teal-300' : 'bg-slate-100 text-slate-700 border border-slate-300'}`}>
              {f.split('-').reverse().join('-')}
              <button type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); quitar(f); }}
                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-rose-100 hover:text-rose-600 text-slate-500"
                aria-label={`Quitar ${f}`}
                data-testid={`btn-quitar-fecha-${f}`}>×</button>
            </span>
          ))}
        </div>
      )}
      {ordenadas.length === 0 && (
        <p className="text-[11px] text-rose-500">Añade al menos una fecha</p>
      )}
    </div>
  );
}
